import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { Form, Formik } from "formik";
import { esignDocSchema } from "formValidations/EsignDocSchema";
import { useToast } from "hooks/useToast";
import { useGetAllChannelMemberQuery } from "store/apis/chat";
import {
  useCreateEnvelopeMutation,
  useCreateEsignCardMutation,
  useLazyInitiateSignatureQuery,
  useSendEnvelopeMutation,
} from "store/apis/eSign";
import { useGetTaskFromChannelIdQuery } from "store/apis/task";
import { useChannelStateContext } from "stream-chat-react";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { EmbedUpdateDocumentV1 } from "@documenso/embed-react";
import { useModal } from "hooks/useModal";
import { useState } from "react";

export const RequestSignModal = ({ close, isOpen }: ModalProps) => {
  const { alertToast } = useToast();
  const { channel } = useChannelStateContext();
  const [createEnvelope] = useCreateEnvelopeMutation();
  const [createEmbedToken] = useLazyInitiateSignatureQuery();
  const [createEsignCard] = useCreateEsignCardMutation();
  const [sendEnvelope] = useSendEnvelopeMutation();

  const embedModal = useModal();
  const [embedToken, setEmbedToken] = useState<string | null>(null);
  const [embedDocumentId, setEmbedDocumentId] = useState<number | null>(null);
  const [envelopeId, setEnvelopeId] = useState<string | null>(null);
  const [envelopeUuid, setEnvelopeUuid] = useState<string | null>(null);

  const { data: currentChannelData } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel?.id!,
    },
    {
      skip: !channel?.id || !isOpen,
    }
  );

  const groupId = currentChannelData?.company_group?.uuid;

  const { data: members, isLoading } = useGetAllChannelMemberQuery(
    {
      groupId: groupId!,
      channelId: channel?.id!,
    },
    {
      skip: !channel?.id || !groupId,
    }
  );

  const onSubmit = async ({
    channelUrl,
    file,
    groupId,
    signerProfileIds,
  }: {
    groupId: string;
    channelUrl: string;
    signerProfileIds: string[];
    file: FileType | null;
  }) => {
    try {
      const fileToSign =
        "uuid" in file! ? { file_id: file.uuid } : { file: file };

      const { uuid, envelope_id } = await createEnvelope({
        groupId: groupId,
        signerProfileIds: signerProfileIds.join(","),
        channelUrl,
        ...fileToSign,
      }).unwrap();

      const { presign_token, document_id } = await createEmbedToken({
        groupId: groupId,
        envelopId: uuid,
        getSenderView: true,
      }).unwrap();

      embedModal.open();
      setEmbedToken(presign_token);
      setEmbedDocumentId(document_id);
      setEnvelopeId(envelope_id);
      setEnvelopeUuid(uuid);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const createChatCard = async () => {
    try {
      await sendEnvelope({
        groupId: groupId as string,
        envelopeId: envelopeUuid!,
      }).unwrap();
      await createEsignCard({
        groupId: groupId as string,
        envelope_id: envelopeId!,
      }).unwrap();
      embedModal.close();
      setEmbedToken(null);
      setEmbedDocumentId(null);
      setEnvelopeId(null);
      setEnvelopeUuid(null);
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Formik
          initialValues={{
            groupId: groupId!,
            channelUrl: channel?.id!,
            signerProfileIds: [],
            file: null,
          }}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange
          validationSchema={esignDocSchema}
        >
          {({ submitForm, isSubmitting, values: { file }, isValid }) => (
            <Modal.Content useCustomOverlay>
              <Modal.Header>
                <Modal.Title>Request sign</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Form className="t-flex t-gap-4 t-flex-col">
                  <FileInput
                    withForm
                    name="file"
                    label="Esign document"
                    file={file}
                    accept={{
                      "application/pdf": [".pdf"],
                    }}
                    withInkleDocs
                    groupId={groupId}
                  />
                  <Combobox
                    required
                    name="signerProfileIds"
                    placeholder="Select signers"
                    withForm
                    isMulti
                    label="Signers"
                    isDisabled={isLoading}
                    isLoading={isLoading}
                    options={members?.map((member) => ({
                      label: member.name,
                      value: member.profile_id,
                    }))}
                    menuPortalTarget={document.body}
                  />
                </Form>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button disabled={isSubmitting} onClick={close}>
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  onClick={submitForm}
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid || !Boolean(file)}
                >
                  Send
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          )}
        </Formik>
      </Modal.Root>

      {embedModal.isOpen && embedToken && embedDocumentId && (
        <Modal.Root open={true} onOpenChange={embedModal.close}>
          <Modal.Content size="xxxl">
            <Modal.Header>
              <Modal.Title>Prepare for e-Sign</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <EmbedUpdateDocumentV1
                darkModeDisabled={true}
                presignToken={embedToken}
                documentId={embedDocumentId}
                onDocumentUpdated={createChatCard}
                className="t-w-full t-h-[80vh]"
              />
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      )}
    </>
  );
};
